/* Good Tree — wholesale quick-order (bulk set cart quantities).
   Each row's quantity is the variant's CURRENT total in the cart (pre-filled on render), not an
   "add this many more" delta — so revisiting the page shows what's already in your bag.
   Progressive enhancement: the <form> posts updates[VARIANT_ID]=QTY to /cart natively with no JS.
   When JS is present we intercept submit, validate quantity rules, POST to /cart/update.js (which
   SETS each listed variant's quantity, matching what the inputs display), then dispatch a
   'cart:updated' event (global.js updates the header bag) + show confirmation. */
(function () {
  'use strict';

  class QuickOrderForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('[data-quick-order-form]');
      if (!this.form) return;

      this.summaryEl = this.querySelector('[data-quick-order-summary]');
      this.errorEl = this.querySelector('[data-quick-order-error]');
      this.confirmEl = this.querySelector('[data-quick-order-confirm]');
      this.submitBtn = this.querySelector('[data-quick-order-submit]');
      this.checkoutBtn = this.querySelector('[data-quick-order-checkout]');
      this.checkoutUrl = this.getAttribute('data-checkout-url') || '/checkout';
      this.inputs = Array.prototype.slice.call(this.querySelectorAll('[data-qty-input]'));

      this.onInput = this.onInput.bind(this);
      this.onSubmit = this.onSubmit.bind(this);
      this.onStep = this.onStep.bind(this);

      this.inputs.forEach((input) => {
        input.addEventListener('input', this.onInput);
        input.addEventListener('change', this.onInput);
      });
      Array.prototype.slice
        .call(this.querySelectorAll('[data-qty-step-up], [data-qty-step-down]'))
        .forEach((btn) => btn.addEventListener('click', this.onStep));
      this.form.addEventListener('submit', this.onSubmit);

      this.updateSummary();
    }

    // Round/clamp a single input to its quantity rules. Returns the normalized value.
    normalize(input) {
      let value = parseInt(input.value, 10);
      if (isNaN(value) || value < 0) value = 0;

      const step = parseInt(input.getAttribute('data-qty-step'), 10) || 1;
      const min = parseInt(input.getAttribute('data-qty-min'), 10) || 1;
      const maxAttr = input.getAttribute('data-qty-max');
      const max = maxAttr ? parseInt(maxAttr, 10) : null;

      if (value > 0) {
        // Respect increment multiples: round up to the nearest step at/above the rule min.
        if (value < min) value = min;
        if (step > 1) value = Math.ceil(value / step) * step;
        if (max !== null && value > max) value = max;
      }
      return value;
    }

    // Non-zero rows only — used for the summary line and the "anything to submit" check.
    collectItems() {
      const items = [];
      this.inputs.forEach((input) => {
        const qty = this.normalize(input);
        if (qty > 0) {
          items.push({ id: parseInt(input.getAttribute('data-variant-id'), 10), quantity: qty });
        }
      });
      return items;
    }

    // Every row, including zeros — /cart/update.js needs an explicit 0 to clear a line that was
    // previously in the cart, so a partial (non-zero-only) payload can't reliably zero things out.
    buildUpdatesMap() {
      const updates = {};
      this.inputs.forEach((input) => {
        updates[input.getAttribute('data-variant-id')] = this.normalize(input);
      });
      return updates;
    }

    hasChanges() {
      return this.inputs.some((input) => {
        const initial = parseInt(input.getAttribute('data-initial-qty'), 10) || 0;
        return this.normalize(input) !== initial;
      });
    }

    onInput() {
      this.clearError();
      this.updateSummary();
    }

    onStep(event) {
      const btn = event.currentTarget;
      const stepper = btn.closest('[data-qty-stepper]');
      const input = stepper && stepper.querySelector('[data-qty-input]');
      if (input) this.step(input, btn.hasAttribute('data-qty-step-up') ? 1 : -1);
    }

    // Move to the next valid quantity: up jumps from 0 to the rule min, then by increment;
    // down steps by increment and clears to 0 once at/below the min.
    step(input, dir) {
      const stepSize = parseInt(input.getAttribute('data-qty-step'), 10) || 1;
      const min = parseInt(input.getAttribute('data-qty-min'), 10) || 1;
      const maxAttr = input.getAttribute('data-qty-max');
      const max = maxAttr ? parseInt(maxAttr, 10) : null;
      let value = parseInt(input.value, 10);
      if (isNaN(value) || value < 0) value = 0;

      if (dir > 0) {
        value = value < min ? min : value + stepSize;
        if (max !== null && value > max) value = max;
      } else {
        value = value <= min ? 0 : value - stepSize;
      }
      input.value = String(value);
      this.onInput();
    }

    updateSummary() {
      if (!this.summaryEl) return;
      const items = this.collectItems();
      const lines = items.length;
      const units = items.reduce((sum, item) => sum + item.quantity, 0);
      if (lines === 0) {
        this.summaryEl.textContent = 'No items selected';
      } else {
        this.summaryEl.textContent =
          lines + (lines === 1 ? ' line · ' : ' lines · ') + units + (units === 1 ? ' unit' : ' units');
      }
    }

    showError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message;
      this.errorEl.hidden = false;
    }

    clearError() {
      if (this.errorEl) {
        this.errorEl.hidden = true;
        this.errorEl.textContent = '';
      }
      this.inputs.forEach((input) => input.classList.remove('is-error'));
    }

    showConfirm(message) {
      if (!this.confirmEl) return;
      this.hideConfirm();
      this.confirmEl.textContent = message;
      this.confirmEl.hidden = false;
      this.confirmTimeout = setTimeout(() => {
        this.confirmEl.classList.add('is-fading');
        this.confirmTimeout = setTimeout(() => this.hideConfirm(), 300);
      }, 5000);
    }

    // Cancels any pending fade-out and hides the confirmation immediately.
    hideConfirm() {
      if (this.confirmTimeout) {
        clearTimeout(this.confirmTimeout);
        this.confirmTimeout = null;
      }
      if (this.confirmEl) {
        this.confirmEl.hidden = true;
        this.confirmEl.classList.remove('is-fading');
      }
    }

    // After a successful update, re-sync every input to what the cart actually holds now
    // (Shopify may have clamped a value against inventory or a quantity rule).
    syncFromCart(cart) {
      this.inputs.forEach((input) => {
        const variantId = parseInt(input.getAttribute('data-variant-id'), 10);
        const match = cart.items.find((item) => item.variant_id === variantId);
        const qty = match ? match.quantity : 0;
        input.value = String(qty);
        input.setAttribute('data-initial-qty', String(qty));
      });
    }

    onSubmit(event) {
      // Snap any out-of-rule values to valid quantities before building the payload.
      this.inputs.forEach((input) => {
        const normalized = this.normalize(input);
        if (String(normalized) !== input.value) input.value = String(normalized);
      });

      const isCheckout = event.submitter === this.checkoutBtn;
      const changed = this.hasChanges();

      if (!isCheckout && !changed) {
        event.preventDefault();
        this.showError('Change a quantity to update your order.');
        return;
      }

      // If fetch is unavailable, let the native form post to /cart (name="checkout" on the
      // clicked button makes Shopify's classic cart endpoint apply updates then redirect there).
      if (typeof window.fetch !== 'function') return;

      event.preventDefault();

      // Nothing to save — checkout is already in sync with what's shown, skip the network round trip.
      if (isCheckout && !changed) {
        window.location.href = this.checkoutUrl;
        return;
      }

      this.setLoading(true);
      this.clearError();
      this.hideConfirm();

      const updates = this.buildUpdatesMap();

      fetch(window.Shopify && window.Shopify.routes ? window.Shopify.routes.root + 'cart/update.js' : '/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates: updates })
      })
        .then((response) => response.json().then((data) => ({ ok: response.ok, data: data })))
        .then((result) => {
          if (!result.ok) {
            const message = (result.data && (result.data.description || result.data.message)) || 'Could not update cart.';
            throw new Error(message);
          }
          return result.data;
        })
        .then((cart) => {
          document.dispatchEvent(
            new CustomEvent('cart:updated', { bubbles: true, detail: { item_count: cart.item_count } })
          );
          if (isCheckout) {
            window.location.href = this.checkoutUrl;
            return;
          }
          this.syncFromCart(cart);
          this.showConfirm('Order updated ✓');
          this.updateSummary();
        })
        .catch((error) => {
          this.showError(error.message || 'Could not update cart.');
        })
        .then(() => {
          this.setLoading(false);
        });
    }

    setLoading(isLoading) {
      if (this.submitBtn) {
        this.submitBtn.disabled = isLoading;
        this.submitBtn.setAttribute('aria-busy', isLoading ? 'true' : 'false');
      }
      if (this.checkoutBtn) {
        this.checkoutBtn.disabled = isLoading;
        this.checkoutBtn.setAttribute('aria-busy', isLoading ? 'true' : 'false');
      }
    }
  }

  if (!customElements.get('quick-order-form')) {
    customElements.define('quick-order-form', QuickOrderForm);
  }
})();
